import React, { useState, useEffect } from 'react';
import PhoneInput, { isValidPhone } from '@/components/common/PhoneInput';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useReferral } from '@/hooks/useReferral';
import { useFormToken } from '@/hooks/useFormToken';
import {
  ArrowRight, Calendar, MapPin, Clock, CheckCircle2, UserCheck,
  Wine, Ticket, Loader2,
} from 'lucide-react';
import { BACKEND_URL } from '@/config/api';

const API = BACKEND_URL;

/*
  /eventos/:slug — Landing dinámica por evento.

  Fuente de datos: GET /api/events/:slug (todo en JSON, sin binarios)
  La imagen del flyer se sirve aparte vía /api/events/:slug/flyer

  Registro: POST /api/events/:slug/register (NO usa /api/leads → soluciona el bug
  del "ya estás registrado" cuando una persona quiere registrarse a varios eventos).

  Sin datos de pago en la web pública: tras la reserva, el equipo escribe con los
  detalles. Los eventos ya celebrados no se muestran (el backend responde 404 con
  PAST_EVENT_DETAIL y aquí se enseña "Este evento ya se celebró").
*/

const PAST_EVENT_DETAIL = 'Este evento ya se celebró';

// Un evento se considera celebrado cuando su día ya ha pasado (hoy sigue visible).
const isPastEvent = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(d);
  day.setHours(0, 0, 0, 0);
  return day < today;
};

const formatEur = (n) => (typeof n === 'number' && n > 0 ? `${n} €` : null);

const CountdownTimer = ({ targetDate, startTime }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!targetDate) return;
    let dateStr = targetDate;
    if (startTime && !dateStr.includes('T')) {
      dateStr = `${dateStr.split('T')[0]}T${startTime}:00`;
    }
    const target = new Date(dateStr);
    if (isNaN(target.getTime())) return;

    const tick = () => {
      const diff = target - new Date();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, ended: true });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        ended: false,
      });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [targetDate, startTime]);

  if (!timeLeft) return null;
  if (timeLeft.ended) return (
    <p className="font-body text-sm text-[#D4AF37]">El evento ya ha comenzado</p>
  );

  return (
    <div className="grid grid-cols-4 gap-2 sm:gap-3 md:gap-4 max-w-md">
      {[
        { value: timeLeft.days, label: 'Días' },
        { value: timeLeft.hours, label: 'Horas' },
        { value: timeLeft.minutes, label: 'Min' },
        { value: timeLeft.seconds, label: 'Seg' },
      ].map((item, idx) => (
        <div key={idx} className="bg-[#0F172A] border border-white/5 px-2 py-3 sm:px-4 sm:py-4 text-center">
          <div className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold leading-none text-[#D4AF37] tabular-nums">
            {String(item.value).padStart(2, '0')}
          </div>
          <div className="font-body text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 mt-1">
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );
};

const formatLongDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(d);
  } catch { return iso; }
};

export default function EventoDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [eventError, setEventError] = useState('');

  const [form, setForm] = useState({ nombre: '', email: '', whatsapp: '' });
  const formToken = useFormToken();  // defensa frente a bots (backend/antibot.py)
  const [ticket, setTicket] = useState('libre');
  const [acceptPolicy, setAcceptPolicy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const { referralCode, affiliateName } = useReferral();

  // Fetch event from API
  useEffect(() => {
    let cancelled = false;
    setLoadingEvent(true);
    axios.get(`${API}/api/events/${slug}`)
      .then((res) => { if (!cancelled) setEvent(res.data); })
      .catch((err) => {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        if (err.response?.status === 404) {
          setEventError(detail === PAST_EVENT_DETAIL ? PAST_EVENT_DETAIL : 'Evento no encontrado');
        } else {
          setEventError('No se pudo cargar el evento');
        }
      })
      .finally(() => { if (!cancelled) setLoadingEvent(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const cocktailEnabled = Boolean(event?.cocktail_enabled);
  const cocktailPrice = formatEur(event?.cocktail_price_eur);

  const scrollToForm = () => document.getElementById('registro')?.scrollIntoView({ behavior: 'smooth' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptPolicy) {
      toast.error('Debes aceptar la política de privacidad');
      return;
    }
    if (!isValidPhone(form.whatsapp)) {
      toast.error('Revisa el número de WhatsApp');
      return;
    }
    setLoading(true);
    try {
      const token = await formToken();
      await axios.post(`${API}/api/events/${slug}/register`, {
        form_token: token || undefined,
        name: form.nombre,
        email: form.email,
        whatsapp: form.whatsapp,
        ticket_type: cocktailEnabled ? ticket : 'libre',
        ...(referralCode ? { referral_code: referralCode } : {}),
      });
      setSubmitted({ ticket: cocktailEnabled ? ticket : 'libre', nombre: form.nombre });
      window.scrollTo({ top: document.getElementById('registro').offsetTop - 80, behavior: 'smooth' });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(!err.response ? 'Error al enviar la solicitud' : (typeof d === 'string' ? d : 'Revisa los datos del formulario'));
    } finally {
      setLoading(false);
    }
  };

  // ── Loading / error states ─────────────────────────────────────────────
  if (loadingEvent) return (
    <div className="min-h-screen bg-[#0a1628] flex items-center justify-center pt-20">
      <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
    </div>
  );
  if (eventError === PAST_EVENT_DETAIL || (event && isPastEvent(event.event_date))) return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col items-center justify-center text-white p-8 text-center" data-testid="event-past">
      <h1 className="font-heading text-2xl sm:text-3xl mb-3">Este evento ya se celebró</h1>
      <p className="font-body text-white/60 mb-6 max-w-md">Consulta los próximos encuentros del club.</p>
      <Link to="/eventos" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F172A] font-semibold px-6 h-12">
        Ver próximos eventos <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
  if (eventError || !event) return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col items-center justify-center text-white p-8 text-center">
      <p className="text-lg mb-4">{eventError || 'Evento no disponible'}</p>
      <Link to="/eventos" className="text-[#D4AF37] hover:underline">Ver todos los eventos</Link>
    </div>
  );

  const flyerSrc = event.flyer_url ? `${API}${event.flyer_url}` : null;
  const longDate = formatLongDate(event.event_date);

  return (
    <div className="min-h-screen bg-white pt-20">

      {/* Hero */}
      <section className="relative flex items-center pt-8 pb-12 lg:pt-12 lg:pb-16 lg:min-h-[80vh] bg-[#0a1628]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Flyer */}
            <div className="flex justify-center lg:justify-end order-1 lg:order-2">
              <div className="relative max-w-xs sm:max-w-sm lg:max-w-md w-full">
                <div className="absolute -inset-1 bg-[#D4AF37]/20 blur-xl" />
                {flyerSrc ? (
                  <img src={flyerSrc} alt={event.title} className="relative w-full h-auto shadow-2xl" />
                ) : (
                  <div className="relative w-full aspect-[3/4] bg-slate-800 flex items-center justify-center text-white/40 shadow-2xl">
                    Sin flyer
                  </div>
                )}
              </div>
            </div>

            {/* Texto */}
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 px-3 py-1.5 sm:px-4 sm:py-2 mb-5 sm:mb-6">
                <Calendar className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-body text-[11px] sm:text-xs text-[#D4AF37] uppercase tracking-widest">
                  {longDate}{event.start_time ? ` · ${event.start_time}h` : ''}
                </span>
              </div>

              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-[1.15] mb-5 sm:mb-6 break-words">
                {event.title}
              </h1>

              {event.subtitle && (
                <p className="font-body text-base sm:text-lg text-white/75 mb-6 sm:mb-8 leading-relaxed">
                  {event.subtitle}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6 sm:mb-8">
                {(event.location_name || event.city) && (
                  <div className="flex items-center gap-2 text-white/70">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="font-body text-sm">{[event.location_name, event.city].filter(Boolean).join(' · ')}</span>
                  </div>
                )}
                {(event.arrival_time || event.start_time) && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="font-body text-sm">
                      {event.arrival_time && `Llegada ${event.arrival_time}h`}
                      {event.arrival_time && event.start_time && ' · '}
                      {event.start_time && `Inicio ${event.start_time}h`}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-white/70">
                  <Ticket className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="font-body text-sm">Plazas limitadas</span>
                </div>
              </div>

              <div className="mb-6 sm:mb-8">
                <p className="font-body text-[11px] sm:text-xs text-white/50 uppercase tracking-widest mb-2 sm:mb-3">Comienza en:</p>
                <CountdownTimer targetDate={event.event_date} startTime={event.start_time} />
              </div>

              <Button
                onClick={scrollToForm}
                className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-bold uppercase tracking-wider rounded-none inline-flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                Reservar mi plaza <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Detalles */}
      <section className="py-14 sm:py-20 md:py-24 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white p-6 sm:p-8 border border-slate-100 text-center">
              <Calendar className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-heading text-lg text-[#0F172A] mb-2">Fecha</h3>
              <p className="font-body text-slate-600 capitalize">{longDate}</p>
              {(event.arrival_time || event.start_time) && (
                <p className="font-body text-sm text-slate-400 mt-1">
                  {event.arrival_time && `Llegada ${event.arrival_time}h`}
                  {event.arrival_time && event.start_time && ' · '}
                  {event.start_time && `Inicio ${event.start_time}h`}
                </p>
              )}
            </div>
            <div className="bg-white p-6 sm:p-8 border border-slate-100 text-center">
              <MapPin className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-heading text-lg text-[#0F172A] mb-2">Ubicación</h3>
              <p className="font-body text-slate-600">{event.location_name || '—'}</p>
              {event.location_address && (
                <p className="font-body text-sm text-slate-400 mt-1">{event.location_address}</p>
              )}
            </div>
            <div className="bg-white p-6 sm:p-8 border border-slate-100 text-center">
              <Ticket className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-heading text-lg text-[#0F172A] mb-2">Acceso</h3>
              <p className="font-body text-slate-600">Inscripción previa</p>
              <p className="font-body text-sm text-slate-400 mt-1">Plazas limitadas</p>
            </div>
          </div>
        </div>
      </section>

      {/* Formulario */}
      <section id="registro" className="py-14 sm:py-20 md:py-28 bg-[#0F172A]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <span className="font-body text-xs uppercase tracking-widest text-[#D4AF37] mb-3 sm:mb-4 block">Reserva</span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 leading-tight">Reserva tu plaza</h2>
            <p className="font-body text-sm sm:text-base text-white/60">Te confirmaremos los detalles de acceso por email.</p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 p-5 sm:p-8 space-y-4 sm:space-y-5">
              {affiliateName && (
                <div className="flex items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 py-3">
                  <UserCheck className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <p className="font-body text-sm text-white">
                    Has sido invitado por <strong className="text-[#D4AF37]">{affiliateName}</strong>
                  </p>
                </div>
              )}

              <div>
                <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-1 block">Nombre completo *</label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="h-12 rounded-none bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                  placeholder="Tu nombre y apellidos" required minLength={2} />
              </div>
              <div>
                <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-1 block">Email *</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-12 rounded-none bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                  placeholder="tu@email.com" required />
              </div>
              <div>
                <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-1 block">WhatsApp *</label>
                <PhoneInput value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} variant="dark" required testId="event-whatsapp" ariaLabel="WhatsApp" />
              </div>

              {/* Plaza: acceso al evento y, solo si el evento lo ofrece, opción con cóctel */}
              <div>
                <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-3 block">Tu plaza</label>
                <div className="space-y-2">
                  <button type="button" onClick={() => setTicket('libre')} data-testid="ticket-libre"
                    className={`w-full text-left p-3 sm:p-4 border-2 transition-colors ${
                      !cocktailEnabled || ticket === 'libre' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 hover:border-white/20'
                    }`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <Ticket className="w-5 h-5 flex-shrink-0 text-[#D4AF37]" />
                        <div className="min-w-0">
                          <p className="font-body text-sm font-semibold text-white">Evento</p>
                          <p className="font-body text-[11px] sm:text-xs text-white/50">Formación + networking</p>
                        </div>
                      </div>
                      <span className="font-body text-sm text-white/70 flex-shrink-0">Sin coste</span>
                    </div>
                  </button>
                  {cocktailEnabled && (
                    <button type="button" onClick={() => setTicket('cocktail')} data-testid="ticket-cocktail"
                      className={`w-full text-left p-3 sm:p-4 border-2 transition-colors ${
                        ticket === 'cocktail' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 hover:border-white/20'
                      }`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                          <Wine className="w-5 h-5 flex-shrink-0 text-[#D4AF37]" />
                          <div className="min-w-0">
                            <p className="font-body text-sm font-semibold text-white">Evento + Cóctel</p>
                            <p className="font-body text-[11px] sm:text-xs text-white/50">Formación + networking + cóctel</p>
                          </div>
                        </div>
                        {cocktailPrice && (
                          <span className="font-heading text-lg sm:text-xl text-[#D4AF37] flex-shrink-0">{cocktailPrice}</span>
                        )}
                      </div>
                    </button>
                  )}
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer pt-2">
                <input type="checkbox" checked={acceptPolicy} onChange={(e) => setAcceptPolicy(e.target.checked)}
                  className="mt-0.5 h-5 w-5 rounded border-white/20 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37]" />
                <span className="font-body text-sm text-white/60 leading-relaxed">
                  He leído y acepto la{' '}
                  <a href="/privacy-policy-fhc" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline hover:text-[#C5A028]">
                    política de privacidad
                  </a>
                </span>
              </label>

              <Button type="submit" disabled={loading || !acceptPolicy}
                className="w-full h-14 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-bold uppercase tracking-wider rounded-none inline-flex items-center justify-center gap-2 disabled:opacity-40">
                {loading ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Enviando...</span>
                ) : (
                  <span className="inline-flex items-center gap-2">Confirmar reserva <ArrowRight className="w-5 h-5" /></span>
                )}
              </Button>
            </form>
          ) : (
            <div className="bg-white/5 border border-[#D4AF37]/30 p-6 sm:p-10 text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-[#0F172A] rounded-full flex items-center justify-center mb-5 sm:mb-6 border border-[#D4AF37]/40">
                <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-[#D4AF37]" />
              </div>
              <h3 className="font-heading text-xl sm:text-2xl md:text-3xl text-white mb-3 leading-tight break-words">
                Reserva recibida{submitted.nombre ? `, ${submitted.nombre.split(' ')[0]}` : ''}
              </h3>

              <p className="font-body text-sm sm:text-base text-white/70 mt-2">
                {submitted.ticket === 'cocktail'
                  ? `Plaza: Evento + Cóctel${cocktailPrice ? ` — ${cocktailPrice}` : ''}.`
                  : 'Plaza: Evento.'}
              </p>
              <p className="font-body text-sm sm:text-base text-white/70 mt-3" data-testid="event-registered-next">
                El equipo te escribirá con los detalles.
              </p>
            </div>
          )}
        </div>
      </section>

    </div>
  );
}
